import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowLeft, Eye, EyeOff, Loader2, Check, Gift, MessageCircle } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import api from '../lib/api';

// Google Icon SVG Component
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

// Check if running as native app (Huawei/non-GMS compatible)
const isNativeApp = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

const Register = () => {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleAvailable, setGoogleAvailable] = useState(!isNativeApp()); // Hide on native by default
  const [hasReferralBonus, setHasReferralBonus] = useState(false);
  
  // WhatsApp opt-in state
  const [whatsappOptIn, setWhatsappOptIn] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [whatsappError, setWhatsappError] = useState('');
  
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Read referral code from URL query parameter (deep link support)
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setReferralCode(refCode.toUpperCase());
      setHasReferralBonus(true);
      toast.success('Referral code applied! Complete registration to claim your $5 bonus.', {
        duration: 5000,
        icon: '🎁'
      });
    }
  }, [searchParams]);

  // Check if Google OAuth is available (hides on Huawei/non-GMS devices)
  useEffect(() => {
    if (isNativeApp()) {
      console.log('[Register] Native app detected - hiding Google OAuth for Huawei/GMS compatibility');
      setGoogleAvailable(false);
    }
  }, []);

  // Google OAuth handler - uses Emergent managed auth service
  const handleGoogleSignup = () => {
    const redirectUrl = window.location.origin;
    const googleAuthUrl = `https://demobackend.emergentagent.com/auth/v1/env/oauth/google?redirect_url=${redirectUrl}`;
    window.location.href = googleAuthUrl;
  };

  const passwordRequirements = [
    { test: password.length >= 8, text: 'At least 8 characters' },
    { test: /[A-Z]/.test(password), text: 'One uppercase letter' },
    { test: /[0-9]/.test(password), text: 'One number' },
  ];

  // Validate WhatsApp number (E.164 format: + followed by 8-15 digits)
  const validateWhatsAppNumber = (num) => {
    if (!num) return '';
    const cleaned = num.replace(/[\s\-\(\)]/g, '');
    const regex = /^\+[1-9]\d{7,14}$/;
    if (!regex.test(cleaned)) {
      return 'Invalid format. Use +[country code][number] (e.g., +31612345678)';
    }
    return '';
  };

  const handleWhatsAppNumberChange = (e) => {
    const value = e.target.value;
    setWhatsappNumber(value);
    if (whatsappOptIn && value) {
      setWhatsappError(validateWhatsAppNumber(value));
    } else {
      setWhatsappError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (!passwordRequirements.every(req => req.test)) {
      toast.error('Please meet all password requirements');
      return;
    }
    
    // Validate WhatsApp if opted in
    if (whatsappOptIn) {
      if (!whatsappNumber) {
        toast.error('Please enter your WhatsApp number');
        return;
      }
      const error = validateWhatsAppNumber(whatsappNumber);
      if (error) {
        toast.error(error);
        return;
      }
    }
    
    setLoading(true);
    
    // Include referral code if provided
    const result = await register(email, password, fullName, referralCode || null);
    
    if (result.success) {
      // If WhatsApp opt-in, save preferences
      if (whatsappOptIn && whatsappNumber) {
        try {
          const locale = (localStorage.getItem('parachpay_language') || navigator.language || 'en').toLowerCase();
          const whatsappLanguage = locale.startsWith('pt') ? 'pt_BR' : locale.startsWith('fr') ? 'fr' : 'en';
          await api.put('/api/users/me/whatsapp-preferences', {
            whatsapp_opt_in: true,
            whatsapp_number: whatsappNumber.replace(/[\s\-\(\)]/g, ''),
            whatsapp_language: whatsappLanguage
          });
        } catch (err) {
          // Non-blocking - preference save failed but account created
          console.error('Failed to save WhatsApp preferences:', err);
        }
      }
      
      // If referral code was used, apply it after registration
      if (referralCode) {
        try {
          await api.post('/api/referrals/apply', null, { params: { referral_code: referralCode } });
          toast.success('Account created with referral bonus! Check your wallet.');
        } catch (err) {
          // Referral application failed, but account was created
          toast.success('Account created successfully!');
        }
      } else {
        toast.success('Account created successfully!');
      }
      navigate('/dashboard');
    } else {
      toast.error(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-background">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-1/4 w-1/2 h-1/2 bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-1/4 w-1/2 h-1/2 bg-teal-500/10 rounded-full blur-3xl" />
      </div>
      
      <div className="w-full max-w-md relative">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors"
          data-testid="back-to-home-link"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>
        
        <Card className="border-border/50 shadow-xl">
          <CardHeader className="space-y-1">
            <div className="flex items-center gap-3 mb-2">
              <img 
                src="https://customer-assets.emergentagent.com/wingman/b0033424-6b4b-45d2-80cb-8129b7f18625/attachments/a2d6607ed0a84e07bd6ec9d382e9f4dc_parachpay_logo_new.jpg" 
                alt="ParachPay" 
                className="h-10 w-auto"
              />
              <span className="font-bold text-xl tracking-wide">PARACHPAY</span>
            </div>
            <CardTitle className="text-2xl font-heading">Create account</CardTitle>
            <CardDescription>
              Get started with ParachPay
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Google Sign Up Button - Hidden on native apps (Huawei/non-GMS) */}
            {googleAvailable && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-12 rounded-xl mb-4 gap-3 font-medium"
                  onClick={handleGoogleSignup}
                  data-testid="google-signup-btn"
                >
                  <GoogleIcon />
                  Continue with Google
                </Button>
                
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-border" />
                  </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">Or continue with email</span>
              </div>
            </div>
              </>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name</Label>
                <Input
                  id="fullName"
                  type="text"
                  placeholder="John Doe"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  className="h-12 rounded-xl"
                  data-testid="fullname-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-12 rounded-xl"
                  data-testid="email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-12 rounded-xl pr-10"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <div className="space-y-1 mt-2">
                  {passwordRequirements.map((req) => (
                    <div key={req.text} className={`flex items-center gap-2 text-xs ${req.test ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}`}>
                      <Check className={`w-3 h-3 ${req.test ? 'opacity-100' : 'opacity-30'}`} />
                      {req.text}
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="h-12 rounded-xl"
                  data-testid="confirm-password-input"
                />
                {confirmPassword && password !== confirmPassword && (
                  <p className="text-xs text-destructive">Passwords do not match</p>
                )}
              </div>

              {/* Referral Code - Optional */}
              <div className="space-y-2">
                <Label htmlFor="referralCode" className="text-sm font-medium flex items-center gap-2">
                  Referral Code {hasReferralBonus ? <span className="text-emerald-500 text-xs flex items-center gap-1"><Gift className="w-3 h-3" /> $5 Bonus!</span> : '(Optional)'}
                </Label>
                <Input
                  id="referralCode"
                  type="text"
                  placeholder="PARACH-XXXXXXXX"
                  value={referralCode}
                  onChange={(e) => {
                    setReferralCode(e.target.value.toUpperCase());
                    setHasReferralBonus(e.target.value.length > 0);
                  }}
                  className={`h-12 rounded-xl ${hasReferralBonus ? 'border-emerald-500 bg-emerald-500/5' : ''}`}
                  data-testid="referral-code-input"
                />
                {hasReferralBonus && (
                  <p className="text-xs text-emerald-500">Complete registration & verify to claim your $5 welcome bonus!</p>
                )}
              </div>

              {/* WhatsApp Notifications - Optional */}
              <div className="space-y-3 p-4 rounded-xl bg-muted/50 border border-border">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="whatsappOptIn"
                    checked={whatsappOptIn}
                    onCheckedChange={setWhatsappOptIn}
                    className="mt-0.5"
                    data-testid="whatsapp-optin-checkbox"
                  />
                  <div className="flex-1">
                    <Label htmlFor="whatsappOptIn" className="text-sm font-medium flex items-center gap-2 cursor-pointer">
                      <MessageCircle className="w-4 h-4 text-green-500" />
                      WhatsApp Notifications
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Send me payment updates on WhatsApp (payment requests, deposits and money received). 
                      Message and data rates may apply. You can turn this off anytime in Settings.
                    </p>
                  </div>
                </div>
                
                {whatsappOptIn && (
                  <div className="space-y-2 mt-3">
                    <Label htmlFor="whatsappNumber" className="text-sm">WhatsApp Number</Label>
                    <Input
                      id="whatsappNumber"
                      type="tel"
                      placeholder="+31612345678"
                      value={whatsappNumber}
                      onChange={handleWhatsAppNumberChange}
                      className={`h-12 rounded-xl ${whatsappError ? 'border-destructive' : ''}`}
                      data-testid="whatsapp-number-input"
                    />
                    {whatsappError && (
                      <p className="text-xs text-destructive">{whatsappError}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Enter your number with country code (e.g., +31 for Netherlands)
                    </p>
                  </div>
                )}
              </div>

              <Button 
                type="submit" 
                className="w-full rounded-xl h-12"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Create Account'
                )}
              </Button>
            </form>
            
            <div className="mt-6 text-center text-sm">
              <span className="text-muted-foreground">Already have an account? </span>
              <Link to="/login" className="text-emerald-600 dark:text-emerald-400 hover:underline font-medium" data-testid="login-link">
                Sign in
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Register;
